import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

/**
 * A reusable hook to manage table state (pagination, limit, search, sorting, and filters)
 * synced with URL query parameters.
 *
 * @param {Object} defaultFilters - Default filter values, pagination, search, and sorting.
 * @returns {Object} The current state and functions to update it.
 */
export const useUrlTableState = (defaultFilters = {}, options = {}) => {
  const { searchKey = 'search' } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Keep track of the default filters keys
  const defaultKeys = Object.keys(defaultFilters);

  // Helper to deserialize values from URL based on default types
  const deserialize = useCallback((key, val, defaultValue) => {
    if (val === null || val === undefined) {
      return defaultValue;
    }
    if (Array.isArray(defaultValue)) {
      return val ? val.split(',') : [];
    }
    if (typeof defaultValue === 'number') {
      const num = Number(val);
      return isNaN(num) ? defaultValue : num;
    }
    if (typeof defaultValue === 'boolean') {
      return val === 'true';
    }
    return val;
  }, []);

  // Helper to serialize values for URL
  const serialize = useCallback((val) => {
    if (Array.isArray(val)) {
      return val.join(',');
    }
    return String(val);
  }, []);

  // Get current state from URL search params with fallback to location.state or defaults
  const getParamValue = useCallback((key) => {
    const urlValue = searchParams.get(key);
    const defaultValue = defaultFilters[key];
    
    // 1. Prioritize URL query param
    if (urlValue !== null) {
      return deserialize(key, urlValue, defaultValue);
    }
    
    // 2. Fall back to location.state filters
    const stateFilters = location.state?.filters || {};
    if (stateFilters[key] !== undefined && stateFilters[key] !== null) {
      return stateFilters[key];
    }
    
    // 3. Fall back to default value
    return defaultValue;
  }, [searchParams, location.state, defaultFilters, deserialize]);

  // Compute filters object dynamically to ensure single source of truth (URL)
  const filters = {};
  defaultKeys.forEach(key => {
    filters[key] = getParamValue(key);
  });

  // Local state for the search input to prevent input lag during typing
  const [searchTerm, setSearchTerm] = useState(filters[searchKey] || '');

  // Keep local searchTerm in sync when the URL/state changes (e.g. Browser Back/Forward)
  useEffect(() => {
    setSearchTerm(filters[searchKey] || '');
  }, [filters[searchKey]]);

  // Ref to hold the debounce timer
  const debounceTimer = useRef(null);

  // Sync location.state to URL query params on mount if URL parameters are empty
  useEffect(() => {
    const stateFilters = location.state?.filters || {};
    const updates = {};
    let shouldUpdate = false;

    defaultKeys.forEach(key => {
      // If URL doesn't have the param, but location state has it
      if (!searchParams.has(key) && stateFilters[key] !== undefined && stateFilters[key] !== null) {
        updates[key] = stateFilters[key];
        shouldUpdate = true;
      }
    });

    if (shouldUpdate) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.keys(updates).forEach(key => {
          next.set(key, serialize(updates[key]));
        });
        return next;
      }, { replace: true });
    }
  }, []);

  // Update URL search parameters
  const updateUrl = useCallback((newParams, replace = false) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.keys(newParams).forEach(key => {
        const val = newParams[key];
        
        // Remove parameter if it's default value or empty
        if (
          val === undefined || 
          val === null || 
          val === '' || 
          val === defaultFilters[key]
        ) {
          next.delete(key);
        } else {
          next.set(key, serialize(val));
        }
      });
      return next;
    }, { replace });
  }, [setSearchParams, defaultFilters, serialize]);

  // Debounced search setter
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Reset page to 1 on search
      updateUrl({ [searchKey]: value, page: 1 });
    }, 500);
  }, [updateUrl, searchKey]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // API Call: Update generic filter field(s)
  const updateFilter = useCallback((newFilters) => {
    // Reset page to 1 when filters change
    const updates = { ...newFilters, page: 1 };
    
    // If search is updated/cleared directly, synchronize state and clear debounce timer
    if (searchKey in newFilters) {
      setSearchTerm(newFilters[searchKey] || '');
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    }

    updateUrl(updates);
  }, [updateUrl, searchKey]);

  // API Call: Update page number
  const updatePage = useCallback((newPage) => {
    updateUrl({ page: newPage });
  }, [updateUrl]);

  // API Call: Update items per page
  const updateLimit = useCallback((newLimit) => {
    // Reset page to 1 when limit changes
    updateUrl({ limit: newLimit, page: 1 });
  }, [updateUrl]);

  // API Call: Update sorting options
  const updateSort = useCallback((sortBy, sortOrder) => {
    updateUrl({ sortBy, sortOrder });
  }, [updateUrl]);

  // API Call: Reset all filters to default values
  const resetFilters = useCallback(() => {
    setSearchTerm('');
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Only remove URL query parameters managed by this hook, preserve others (e.g. tab)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      defaultKeys.forEach(key => next.delete(key));
      return next;
    }, { replace: true });
  }, [setSearchParams, defaultKeys]);

  return {
    filters,
    searchTerm,
    setSearchTerm: handleSearchChange,
    updateFilter,
    updatePage,
    updateLimit,
    updateSort,
    resetFilters
  };
};

export default useUrlTableState;
